/**
 * ResultsScreen.jsx
 */

import React, { useState, useMemo } from 'react';
import { PartnerPromoCard } from './PartnerPromo';
import NextGameNudge from './NextGameNudge';
import { ntgJrShareText } from '../utils/ntgShare';

const SD_QUESTIONS_PER_ROUND = 1;

const NTG_RAIN_EMOJIS = ['🏈', '🏀', '⚾', '⚽', '🏒', '⛳', '🎾'];
const MONEY_RAIN_EMOJIS = ['💰', '💵', '💸'];

// ── Flag Modal ────────────────────────────────────────────────────
// Category first, then the detail. A bare text box was making Matt guess
// whether "wrong pic" meant the photo or the answer, so the report now
// carries its own bucket. Identity is captured automatically when we have
// it and only asked for when we don't.
const FLAG_CATEGORIES = [
  { key: 'image', label: 'IMAGE', hint: 'Wrong or broken photo' },
  { key: 'answer', label: 'ANSWER', hint: 'Wrong college or answer' },
  { key: 'other', label: 'OTHER', hint: 'Anything else' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FlagModal = ({ onClose, username, email }) => {
  const [category, setCategory] = useState('');
  const [details, setDetails] = useState('');
  // Signed-in reporters never see this field; we already know who they are.
  const [guestEmail, setGuestEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const signedIn = !!(username || email);
  const guestEmailValid = !guestEmail.trim() || EMAIL_RE.test(guestEmail.trim());
  const canSend = !!category && !!details.trim() && guestEmailValid;

  const handleSend = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      await fetch('/api/flag-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          details: details.trim(),
          username: username || null,
          email: (email || guestEmail.trim() || null),
          signedIn,
          page: typeof window !== 'undefined' ? window.location.pathname : null,
        }),
      });
    } catch (e) {
      // Never leave the reporter staring at a spinner. A dropped flag is a
      // far smaller problem than a modal that will not close.
    }
    setSent(true);
    setSending(false);
  };

  const pillStyle = (active) => ({
    flex: 1,
    padding: '8px 4px',
    background: active ? 'rgba(201,168,76,0.15)' : '#111b30',
    border: `1px solid ${active ? 'var(--gold)' : 'var(--border-dark)'}`,
    borderRadius: 'var(--radius-sm)',
    color: active ? 'var(--gold)' : 'var(--text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: '11px',
    letterSpacing: '1px',
    cursor: 'pointer',
  });

  const fieldStyle = {
    width: '100%', padding: '10px 12px', background: '#111b30',
    border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)',
    color: 'var(--text-white)', fontFamily: 'var(--font-heading)',
    fontSize: '13px', letterSpacing: '1px', boxSizing: 'border-box',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: '20px',
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-dark)',
        borderRadius: 'var(--radius)', padding: '24px', width: '100%', maxWidth: '320px',
      }}>
        {sent ? (
          <>
            <div style={{ textAlign: 'center', fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--green-correct)', letterSpacing: '2px', marginBottom: '8px' }}>✓ REPORTED</div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-dim)', textAlign: 'center', marginBottom: '16px' }}>Thanks — we'll get it fixed.</p>
            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={onClose}>CLOSE</button>
          </>
        ) : (
          <>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', letterSpacing: '2px', color: 'var(--gold)', marginBottom: '10px' }}>🚩 FLAG AN ISSUE</div>

            <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', marginBottom: '6px', letterSpacing: '1px' }}>WHAT KIND OF ISSUE?</p>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
              {FLAG_CATEGORIES.map((c) => (
                <button key={c.key} type="button" style={pillStyle(category === c.key)} onClick={() => setCategory(c.key)}>
                  {c.label}
                </button>
              ))}
            </div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--text-dim)', minHeight: '14px', marginBottom: '12px' }}>
              {FLAG_CATEGORIES.find((c) => c.key === category)?.hint || ''}
            </p>

            <textarea
              placeholder={category === 'answer' ? 'Which player, and what should it say?' : category === 'image' ? 'Which player has the wrong photo?' : 'What happened?'}
              value={details}
              onChange={e => setDetails(e.target.value)}
              maxLength={500}
              rows={3}
              style={{ ...fieldStyle, marginBottom: '12px', resize: 'vertical', lineHeight: '1.4' }}
            />

            {signedIn ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', marginBottom: '12px' }}>
                Reporting as <strong style={{ color: 'var(--gold)' }}>{username || email}</strong>
              </p>
            ) : (
              <>
                <input
                  type="email"
                  placeholder="Email (optional, so we can follow up)"
                  value={guestEmail}
                  onChange={e => setGuestEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  style={{ ...fieldStyle, marginBottom: guestEmailValid ? '12px' : '4px', borderColor: guestEmailValid ? 'var(--border-dark)' : 'var(--red)' }}
                />
                {!guestEmailValid && (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--red)', marginBottom: '12px' }}>That does not look like an email address.</p>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>CANCEL</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSend} disabled={sending || !canSend}>
                {sending ? 'SENDING...' : 'SEND'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const ResultsScreen = ({
  isDailyChallenge,
  isNBADaily,
  isMoshDaily,
  isNTGDaily,
  isDigitsDaily,
  dailyResult,
  claimedStreak,
  currentUser,
  playerNumber,
  myScore,
  opponentScore,
  opponentAnswers,
  myAnswers,
  gameCode,
  sport = 'nfl',
  mode,
  totalQuestions,
  isWaitingForOpponent,
  needsSuddenDeath = false,
  sdScore = 0,
  sdAnswers = [],
  sdScores = [],
  opponentSdAnswers = [],
  outcome,
  onRematch,
  onNewGame,
  onGoHome,
  onPlaySuddenDeath,
  p1Username = 'PLAYER 1',
  p2Username = 'PLAYER 2',
  onShowAuth,
  onPlayDaily,
}) => {
  const [rematching, setRematching] = useState(false);
  const [showFlag, setShowFlag] = useState(false);

  const handleShare = async (score, streak) => {
  if (isNTGDaily) {
    const text = ntgJrShareText(score >= 1);
    try {
      if (navigator.share) await navigator.share({ title: 'Know Your Guy', text });
      else { await navigator.clipboard.writeText(text); alert('Copied to clipboard.'); }
    } catch {}
    return;
  }
  if (isDigitsDaily) {
    const text = score >= 1
      ? `Called today's Digits #️⃣ on KYG. ${streak > 0 ? `${streak}-day streak. ` : ''}Which guy wore it? knowyourguy.com`
      : `Today's Digits number got me #️⃣💀 Bet you don't know which guy wore it. knowyourguy.com`;
    try {
      if (navigator.share) await navigator.share({ title: 'Know Your Guy', text });
      else { await navigator.clipboard.writeText(text); alert('Copied to clipboard.'); }
    } catch {}
    return;
  }
  const emoji = isMoshDaily ? '🍲' : isNBADaily ? '🏀' : '🏈';
  const sport = isMoshDaily ? 'Mish Mosh' : isNBADaily ? 'NBA' : 'NFL';
  const messages = {
    5: `5/5 on today's Know Your Guy ${sport} Daily Challenge ${emoji}🔥 ${streak > 0 ? `${streak}-day streak.` : ''} Do you know your guy? knowyourguy.com`,
    4: `4/5 on today's Know Your Guy ${sport} Daily Challenge ${emoji} So close. Can you do better? knowyourguy.com`,
    3: `Barely survived today's Know Your Guy ${sport} Daily Challenge ${emoji} 3/5. Think you can do better? knowyourguy.com`,
    2: `Got humbled by today's Know Your Guy ${sport} Daily Challenge 😅 2/5. Yikes. Bet you can't do better. knowyourguy.com`,
    1: `1/5 on today's Know Your Guy ${sport} Daily Challenge 💀 Don't ask. knowyourguy.com`,
    0: `0/5 on today's Know Your Guy ${sport} Daily Challenge 💀 Don't even ask. knowyourguy.com`,
  };
  const text = messages[score] ?? `${score}/5 on today's Know Your Guy ${sport} Daily Challenge ${emoji} knowyourguy.com`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Know Your Guy', text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard.');
      }
    } catch {}
  };

  // One rain for every mode. isXDaily covers the dailies, sport covers H2H.
  const footballs = useMemo(() => {
    const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const pick = () => {
      if (isNTGDaily) return rand(NTG_RAIN_EMOJIS);
      if (isDigitsDaily) return '#️⃣';
      if (isMoshDaily || sport === 'mosh') return rand(MONEY_RAIN_EMOJIS);
      if (isNBADaily || sport === 'nba') return '🏀';
      return '🏈';
    };
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 3,
      size: 20 + Math.random() * 20,
      emoji: pick(),
    }));
  }, [isNTGDaily, isDigitsDaily, isNBADaily, isMoshDaily, sport]);

  // The Mish Mosh money rain used to live here as a hand-built DOM effect:
  // 60 nodes appended to document.body, `forwards` so each symbol animated
  // exactly once, and a 5s timeout that ripped the container out. That is why
  // it fell once and stopped while the footballs and basketballs kept looping.
  // It now rides the shared .football-rain path, which is `infinite` and
  // already rotates 720deg, so Mosh loops like every other mode.



  // ── Daily Challenge Results ───────────────────────────────────────
  if (isDailyChallenge) {
    const passed = dailyResult ? dailyResult.passed : myScore >= 3;
    const streak = (claimedStreak != null) ? claimedStreak : (dailyResult ? dailyResult.streak : 0);
    const ntgStrikes = (isNTGDaily || isDigitsDaily) && dailyResult ? (dailyResult.strikes || 0) : 0;

    return (
      <div className="results-screen" style={{ position: 'relative' }}>
        {showFlag && <FlagModal username={currentUser?.username} email={currentUser?.email} onClose={() => setShowFlag(false)} />}
        {passed && (
  <div className="football-rain" aria-hidden="true">
    {footballs.map((fb) => (
      <span key={fb.id} className="football-drop" style={{ left: `${fb.left}%`, animationDelay: `${fb.delay}s`, animationDuration: `${fb.duration}s`, fontSize: `${fb.size}px` }}>
        {fb.emoji}
      </span>
    ))}
  </div>
)}
{/* No JSX rain for Mosh: the money-rain effect above already renders one.
    Both used to fire at once, 30 drops here plus 60 there. */}
        <div className="results-card" style={{ position: 'relative' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
    <button className="btn-home-corner" onClick={onGoHome} style={{ position: 'static' }}>HOME</button>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', margin: 0, opacity: 0.8 }}>
      feedback? <a href="mailto:matt@knowyourguy.com" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>matt@knowyourguy.com</a>
    </p>
    <button onClick={() => setShowFlag(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px', padding: '4px 8px' }}>🚩 flag an issue</button>
  </div>
 {!currentUser && passed && (
  <div style={{ background: 'var(--bg-navy)', border: '1px solid var(--gold)', borderRadius: 'var(--radius)', padding: '12px', marginBottom: '8px', textAlign: 'center' }}>
    <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--gold)', letterSpacing: '2px', marginBottom: '6px' }}>SAVE THIS WIN TO YOUR STREAK</p>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', lineHeight: '1.6', marginBottom: '10px' }}>Today's win counts toward your streak, plus The Handcrafted Daily (<a href="/sample-daily.html" target="_blank" rel="noreferrer" style={{ color: 'var(--gold)' }}>sample</a>)</p>
    <button className="btn btn-primary" style={{ marginTop: '0' }} onClick={onShowAuth}>sign up...</button>
  </div>
)}
{!currentUser && !passed && (
  <div style={{ background: 'var(--bg-navy)', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)', padding: '12px', marginBottom: '8px', textAlign: 'center' }}>
    <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--text-dim)', letterSpacing: '2px', marginBottom: '6px' }}>SIGN IN TO UNLOCK MORE</p>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', lineHeight: '1.6', marginBottom: '10px' }}>
      View the leaderboard<br />Challenge friends head to head<br />Track your daily streak and accuracy<br />Earn sweet, sweet pieces <span style={{ fontSize: '0.85em', opacity: 0.75 }}>(while supplies last)</span><br />The Handcrafted Daily (<a href="/sample-daily.html" target="_blank" rel="noreferrer" style={{ color: 'var(--gold)' }}>sample</a>) (opt out whenever)
    </p>
    <button className="btn btn-outline" style={{ marginTop: '0' }} onClick={onShowAuth}>SIGN IN / SIGN UP</button>
  </div>
)}

{/* Outcome + score in ONE compact card. These used to be two tall stacked
    blocks (64px score digit, 36px headline) that pushed the free-dailies
    nudge below the fold on a phone. Kept as daily-only classes so the Guy Off
    screens below still use the original full-size treatment. */}
<div className={`daily-cap ${passed ? 'winner' : 'loser'}`}>
  {passed ? (
    <>
      <p className="daily-cap-eyebrow">&#127942; EASY WORK</p>
      <p className="daily-cap-headline">YOU KNOW YOUR GUY!</p>
    </>
  ) : (
    <>
      <p className="daily-cap-eyebrow">TOUGH ONE</p>
      <p className="daily-cap-headline loser">YOU DON&apos;T KNOW YOUR GUY...</p>
    </>
  )}
  <p className="daily-cap-score">
    TODAY&apos;S SCORE <strong>{myScore}</strong><span>/{totalQuestions}</span>
  </p>
</div>

{currentUser && (
  <div className={`results-banner ${streak > 0 ? 'winner' : 'tie'}`} style={{ padding: '10px 14px' }}>
    {/* streak === 0 means the streak is gone. A genuine win never lands here (the RPC
        win branch always yields >= 1), so the only correct copy is the reset message.
        Previously this showed a misleading "STREAK SAVED!" on a same-day replay. */}
    {streak > 0 ? (
      <div className="streak-row">
        
        <span className="results-label" style={{ color: 'var(--gold)', fontSize: '12px', letterSpacing: '2px' }}>CURRENT STREAK</span>
        <span className="streak-row-num">{streak}</span>
        <span className="streak-row-unit">{streak === 1 ? 'DAY' : 'DAYS'}</span>
      </div>
    ) : (
      <div className="results-label">STREAK RESET - COME BACK TOMORROW</div>
    )}
    {(isNTGDaily || isDigitsDaily) && (
      <div style={{ marginTop: '8px', fontFamily: 'var(--font-heading)', color: 'var(--text-dim)' }}>
        <div style={{ fontSize: '15px', letterSpacing: '1px', textTransform: 'none' }}>
          stri<span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.1em', verticalAlign: 'middle' }}>K</span>e: {ntgStrikes >= 1 ? ntgStrikes : '-'}
        </div>
        <div style={{ fontSize: '10px', letterSpacing: '1px', marginTop: '2px' }}>(3 strikes your streak resets)</div>
      </div>
    )}
  </div>
)}

{myAnswers && myAnswers.length > 0 && (
  <div className="results-breakdown">
    <h3>YOUR ANSWERS</h3>
    <div className="breakdown-list">
      {myAnswers.map((a, idx) => (
        <div key={idx} className={`breakdown-item ${a.isCorrect ? 'correct' : 'incorrect'}`}>
          <span className="breakdown-icon">{a.isCorrect ? '✓' : '✗'}</span>
          <span className="breakdown-player">{a.player}</span>
          <span className="breakdown-college">{a.college}</span>
        </div>
      ))}
    </div>
  </div>
)}

<NextGameNudge
  currentGame={isMoshDaily ? 'mosh' : isNBADaily ? 'nba' : isNTGDaily ? 'jr' : isDigitsDaily ? 'digits' : 'nfl'}
  currentUser={currentUser}
  onPlayDaily={onPlayDaily}
  onShowAuth={onShowAuth}
/>

<div className="results-actions" style={{ flexDirection: 'row', gap: '8px' }}>
  <button className="btn btn-secondary" style={{ flex: 1, marginTop: 0 }} onClick={() => handleShare(myScore, dailyResult?.streak || 0)}>SHARE</button>
  <button className="btn btn-secondary" style={{ flex: 1, marginTop: 0 }} onClick={onGoHome}>HOME</button>
</div>
        <PartnerPromoCard />
        </div>
      </div>
    );
  }

  // ── Waiting for opponent (regulation) ────────────────────────────
  if (isWaitingForOpponent) {
  return (
    <div className="results-screen" style={{ position: 'relative' }}>
      {showFlag && <FlagModal username={currentUser?.username} email={currentUser?.email} onClose={() => setShowFlag(false)} />}
      <div className="results-card" style={{ position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <button className="btn-home-corner" onClick={onGoHome} style={{ position: 'static' }}>HOME</button>
          <button onClick={() => setShowFlag(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px', padding: '4px 8px' }}>🚩 flag an issue</button>
        </div>
       {playerNumber === 1 && (
  <>
    <p style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', color: 'var(--text-dim)', letterSpacing: '2px', textAlign: 'center', marginBottom: '6px' }}>IMPRESS EVERYONE YOU KNOW</p>
    <button
      className="btn btn-primary"
      style={{ width: '100%', marginBottom: '16px' }}
      onClick={async () => {
    const url = `${window.location.origin}/join/${gameCode}`;
    const emoji = sport === 'nba' ? '🏀' : sport === 'mosh' ? '🍲' : '🏈';
const sportLabel = sport === 'nba' ? 'NBA' : sport === 'mosh' ? 'Mish Mosh' : 'NFL';
const text = `Do you know your guy? Accept my ${sportLabel} challenge ${emoji} ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Know Your Guy', text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard.');
      }
    } catch {}
 }}
    >
      ⚔️ CHALLENGE A FRIEND
    </button>
  </>
)}
        <div className="results-banner waiting">
          <h1 className="results-title">GAME COMPLETE</h1>
          <p className="results-message">Waiting for your opponent to finish...</p>
        </div>
        <div className="results-your-score">
          <span className="results-label">YOUR SCORE</span>
          <span className="results-score-big">{myScore}</span>
          <span className="results-score-total">/ {totalQuestions}</span>
        </div>
        <div className="results-waiting">
          <div className="waiting-spinner" />
          <p>WAITING FOR OPPONENT...</p>
        </div>
      </div>
    </div>
  );
}

  // ── Tied — needs sudden death ─────────────────────────────────────
  if (needsSuddenDeath) {
    return (
      <div className="results-screen" style={{ position: 'relative' }}>
        {showFlag && <FlagModal username={currentUser?.username} email={currentUser?.email} onClose={() => setShowFlag(false)} />}
        <div className="results-card" style={{ position: 'relative' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
    <button className="btn-home-corner" onClick={onGoHome} style={{ position: 'static' }}>HOME</button>
    <button onClick={() => setShowFlag(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px', padding: '4px 8px' }}>🚩 flag an issue</button>
  </div>
          <div className="results-banner tie">
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>⚡</div>
            <h1 className="results-title">IT'S TIED!</h1>
            <p className="results-message">Regulation ended {myScore} — {opponentScore}. Time for sudden death.</p>
          </div>
          <div className="results-your-score">
            <span className="results-label">REGULATION SCORE</span>
            <span className="results-score-big">{myScore}</span>
            <span className="results-score-total">/ {totalQuestions}</span>
          </div>
          <div style={{ padding: '16px', background: 'var(--bg-navy)', borderRadius: 'var(--radius)', border: '1px solid var(--border-dark)', margin: '0 0 8px' }}>
            <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--text-dim)', letterSpacing: '2px', textAlign: 'center', marginBottom: '8px' }}>ONE QUESTION — GET IT RIGHT TO WIN</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-dim)', textAlign: 'center' }}>Answer 1 question. If you both get it right or both miss — next question.</p>
          </div>
          <div className="results-actions">
            <button className="btn btn-primary" onClick={onPlaySuddenDeath}>⚡ PLAY SUDDEN DEATH</button>
            <button className="btn btn-secondary" onClick={onGoHome}>BACK TO HOME</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Both players done — final result ─────────────────────────────
  let winner = null;
  let finalP1Score = playerNumber === 1 ? myScore : opponentScore;
  let finalP2Score = playerNumber === 2 ? myScore : opponentScore;

  if (outcome === 'win') winner = playerNumber;
  else if (outcome === 'loss') winner = playerNumber === 1 ? 2 : 1;
  else if (finalP1Score > finalP2Score) winner = 1;
  else if (finalP2Score > finalP1Score) winner = 2;

  const iWon = outcome === 'win' || winner === playerNumber;
  const isTie = outcome === 'tie' && winner === null;

  const allQuestions = myAnswers || [];
  const p1Answers = playerNumber === 1 ? myAnswers : opponentAnswers;
  const p2Answers = playerNumber === 2 ? myAnswers : opponentAnswers;

  const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '0', width: '100%' };
  const cellStyle = (align = 'left') => ({
    fontFamily: 'var(--font-body)', fontSize: '11px', padding: '6px 4px',
    borderBottom: '1px solid var(--border-dark)', textAlign: align,
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
  });
  const checkStyle = (correct, exists) => ({
    ...cellStyle('center'),
    color: !exists ? 'var(--text-dim)' : correct ? 'var(--green-correct)' : 'var(--red-incorrect)',
    fontWeight: 700, fontSize: '14px',
  });

  return (
    <div className="results-screen" style={{ position: 'relative' }}>
      {showFlag && <FlagModal username={currentUser?.username} email={currentUser?.email} onClose={() => setShowFlag(false)} />}
      {iWon && (
  <div className="football-rain" aria-hidden="true">
    {footballs.map((fb) => (
      <span key={fb.id} className="football-drop" style={{ left: `${fb.left}%`, animationDelay: `${fb.delay}s`, animationDuration: `${fb.duration}s`, fontSize: `${fb.size}px` }}>
        {fb.emoji}
      </span>
    ))}
  </div>
)}

      <div className="results-card" style={{ position: 'relative' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
    <button className="btn-home-corner" onClick={onGoHome} style={{ position: 'static' }}>HOME</button>
    <button onClick={() => setShowFlag(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px', padding: '4px 8px' }}>🚩 flag an issue</button>
  </div>
        <div className={`results-banner ${iWon ? 'winner' : isTie ? 'tie' : 'loser'}`}>
          {isTie ? (
            <h1 className="results-title">TIE GAME</h1>
          ) : iWon ? (
            <>
              <div className="winner-animation"><span className="trophy">&#127942;</span></div>
              <h1 className="results-title">YOU WIN!</h1>
              <p className="results-message">You know your guy!</p>
            </>
          ) : (
            <>
              <h1 className="results-title">DEFEATED</h1>
              <p className="results-message">You Don't Know Your Guy.</p>
            </>
          )}
        </div>

        <div className="results-boxscore">
          <div className="boxscore-row boxscore-header">
            <span className="boxscore-label"></span>
            <span className={`boxscore-col ${winner === 1 ? 'boxscore-winner-name' : ''}`}>{p1Username}</span>
            <span className={`boxscore-col ${winner === 2 ? 'boxscore-winner-name' : ''}`}>{p2Username}</span>
          </div>
          <div className="boxscore-row">
            <span className="boxscore-label">REGULATION</span>
            <span className={`boxscore-col ${winner === 1 ? 'boxscore-winner-name' : ''}`}>{finalP1Score}</span>
            <span className={`boxscore-col ${winner === 2 ? 'boxscore-winner-name' : ''}`}>{finalP2Score}</span>
          </div>
          {sdScores.map((r, i) => {
            const sdP1 = playerNumber === 1 ? r.p1 : r.p2;
            const sdP2 = playerNumber === 2 ? r.p1 : r.p2;
            return (
              <div key={i} className="boxscore-row">
                <span className="boxscore-label">SD ROUND {r.round + 1}</span>
                <span className={`boxscore-col ${sdP1 > sdP2 ? 'boxscore-winner-name' : ''}`}>{sdP1 ?? '—'}</span>
                <span className={`boxscore-col ${sdP2 > sdP1 ? 'boxscore-winner-name' : ''}`}>{sdP2 ?? '—'}</span>
              </div>
            );
          })}
        </div>

        {allQuestions.length > 0 && (
          <div className="results-breakdown">
            <h3>REGULATION ANSWERS</h3>
            <div style={gridStyle}>
              <div style={{ ...cellStyle(), fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px' }}>PLAYER</div>
              <div style={{ ...cellStyle(), fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px' }}>COLLEGE</div>
              <div style={{ ...cellStyle('center'), fontFamily: 'var(--font-heading)', fontSize: '10px', color: winner === 1 ? 'var(--gold)' : 'var(--text-dim)', letterSpacing: '1px', minWidth: '52px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>{p1Username}</div>
              <div style={{ ...cellStyle('center'), fontFamily: 'var(--font-heading)', fontSize: '10px', color: winner === 2 ? 'var(--gold)' : 'var(--text-dim)', letterSpacing: '1px', minWidth: '52px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>{p2Username}</div>
              {allQuestions.map((a, idx) => {
                const p1a = p1Answers?.[idx];
                const p2a = p2Answers?.[idx];
                return (
                  <React.Fragment key={idx}>
                    <div style={cellStyle()}>{a.player}</div>
                    <div style={{ ...cellStyle(), color: 'var(--gold)' }}>{a.college}</div>
                    <div style={checkStyle(p1a?.isCorrect, !!p1a)}>{p1a ? (p1a.isCorrect ? '✓' : '✗') : '—'}</div>
                    <div style={checkStyle(p2a?.isCorrect, !!p2a)}>{p2a ? (p2a.isCorrect ? '✓' : '✗') : '—'}</div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {sdAnswers.length > 0 && (
          <div className="results-breakdown" style={{ marginTop: '12px' }}>
            <h3>⚡ SUDDEN DEATH</h3>
            <div style={gridStyle}>
              <div style={{ ...cellStyle(), fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px' }}>PLAYER</div>
              <div style={{ ...cellStyle(), fontFamily: 'var(--font-heading)', fontSize: '10px', color: 'var(--text-dim)', letterSpacing: '1px' }}>COLLEGE</div>
              <div style={{ ...cellStyle('center'), fontFamily: 'var(--font-heading)', fontSize: '10px', color: winner === 1 ? 'var(--gold)' : 'var(--text-dim)', letterSpacing: '1px', minWidth: '36px' }}>{p1Username.split(' ')[0]}</div>
              <div style={{ ...cellStyle('center'), fontFamily: 'var(--font-heading)', fontSize: '10px', color: winner === 2 ? 'var(--gold)' : 'var(--text-dim)', letterSpacing: '1px', minWidth: '36px' }}>{p2Username.split(' ')[0]}</div>
              {sdAnswers.map((a, idx) => {
  const oppA = opponentSdAnswers?.[idx];
  const p1a = playerNumber === 1 ? a : oppA;
  const p2a = playerNumber === 1 ? oppA : a;
                return (
                  <React.Fragment key={idx}>
                    <div style={cellStyle()}>{a.player} <span style={{ color: 'var(--text-dim)', fontSize: '10px' }}>R{a.sdRound + 1}</span></div>
                    <div style={{ ...cellStyle(), color: 'var(--gold)' }}>{a.college}</div>
                    <div style={checkStyle(p1a?.isCorrect, !!p1a)}>{p1a ? (p1a.isCorrect ? '✓' : '✗') : '—'}</div>
                    <div style={checkStyle(p2a?.isCorrect, !!p2a)}>{p2a ? (p2a.isCorrect ? '✓' : '✗') : '—'}</div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        <div className="results-actions">
          {onRematch && (
            <button className="btn btn-primary" onClick={() => { setRematching(true); onRematch(); }} disabled={rematching}>
              {rematching ? 'STARTING...' : 'REMATCH'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onGoHome}>BACK TO HOME</button>
        </div>
        <PartnerPromoCard />
      </div>
    </div>
  );
};

export default ResultsScreen;